
import axios from "axios";
import { API_BASE } from "./apiBase";
import { apiRefreshSession } from "../containers/newBackendApi/auth";

const getCookie = (name) => {
  const cookies = document.cookie.split("; ");
  for (let cookie of cookies) {
    const [key, value] = cookie.split("=");
    if (key === name) {
      try {
        return decodeURIComponent(value);
      } catch (error) {
        return value;
      }
    }
  }
  return null;
};

const isActiveJwt = (token) => {
  if (!token) return false;
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(
      atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))
    );
    return Number(claims.exp) > Math.floor(Date.now() / 1000);
  } catch (error) {
    return false;
  }
};

const getToken = () => {
  const localToken = localStorage.getItem("tk");
  const sharedToken = getCookie("VCS_TOKEN");

  if (isActiveJwt(localToken)) return localToken;
  if (isActiveJwt(sharedToken)) return sharedToken;
  return null;
};

const axiosInstance = axios.create({
  // Resolved at runtime so a build that baked a localhost URL doesn't make the
  // deployed site call localhost. See src/lib/apiBase.js.
  baseURL: API_BASE,
  timeout: 10000,
  // Send the VCS_SESSION cookie with every request (mirrors the accounts app).
  // Session-cookie endpoints (/auth/me, /auth/organisations) need it; the bearer
  // header below still rides along for the JWT-authed developer endpoints.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// A single in-flight /auth/refresh shared by every request that 401s while it is
// running, so N concurrent 401s trigger one rotation instead of N.
let refreshPromise = null;
const refreshSessionOnce = () => {
  if (!refreshPromise) {
    refreshPromise = apiRefreshSession().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// On a 401, rotate the session once and replay. A failed refresh is NOT a logout
// signal (it legitimately 401s when the session has no server-side refresh token
// while VCS_SESSION is still valid); session liveness is owned by the auth check,
// not by this interceptor — so a persistent 401 just propagates.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }
    if (!originalRequest || originalRequest._retried || originalRequest.url === "/auth/refresh") {
      return Promise.reject(error);
    }
    originalRequest._retried = true;

    try {
      const data = await refreshSessionOnce();
      const idToken = data?.id_token;
      if (idToken) {
        localStorage.setItem("tk", idToken);
        const exp = (() => {
          try {
            const payload = idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
            return JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, "="))).exp;
          } catch {
            return null;
          }
        })();
        if (exp) localStorage.setItem("ext", String(exp));
      }
      return axiosInstance(originalRequest);
    } catch {
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
